/**
 * useCvVersions
 *
 * Owns the "CV versions" management flow (create / rename / delete /
 * duplicate / import from JSON backup). Previously these lived inline
 * in App.tsx as 7 arrow functions plus a local `normalizeTemplateName`
 * helper. Extracting them here isolates the version surface so the
 * <EditSidebar> can stay purely presentational.
 *
 * Owned state:
 *   - newTemplateName        : input value for the "add version" field
 *   - editingTemplateId      : id of the version being renamed (or null)
 *   - editingTemplateName    : draft name while renaming
 *
 * Takes CV-state setters from useCvState (`setAllTemplatesData`,
 * `setTemplateMeta`, `setActiveTemplate`) and the read-only snapshots
 * it needs to compute "existing ids" and "fallback data" when
 * creating a new version.
 */

import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import type { CVData } from '../types';
import { defaultCV } from '../types';
import { cloneCvData, makeTemplateId } from '../lib/cv-utils';
import type { TemplateMetaItem } from './useCvState';
import { toast } from '../lib/toast';

const normalizeTemplateName = (name: string) => name.trim();

export interface UseCvVersionsParams {
  activeTemplate: string;
  templateMeta: TemplateMetaItem[];
  allTemplatesData: Record<string, CVData>;
  setActiveTemplate: Dispatch<SetStateAction<string>>;
  setTemplateMeta: Dispatch<SetStateAction<TemplateMetaItem[]>>;
  setAllTemplatesData: Dispatch<SetStateAction<Record<string, CVData>>>;
  /** i18n translator — used for the default name of imported backups. */
  t: (key: string) => string;
}

export interface UseCvVersionsReturn {
  // Local input state
  newTemplateName: string;
  setNewTemplateName: (v: string) => void;
  editingTemplateId: string | null;
  editingTemplateName: string;
  setEditingTemplateName: (v: string) => void;

  // Actions
  handleAddTemplate: () => void;
  handleStartRenameTemplate: (id: string, name: string) => void;
  handleCancelRenameTemplate: () => void;
  handleSaveRenameTemplate: () => void;
  handleDeleteTemplate: (id: string) => void;
  handleDuplicateTemplate: (id: string) => void;
  handleImportJSON: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  renameTemplate: (id: string, name: string) => void;
}

export function useCvVersions(params: UseCvVersionsParams): UseCvVersionsReturn {
  const {
    activeTemplate,
    templateMeta,
    allTemplatesData,
    setActiveTemplate,
    setTemplateMeta,
    setAllTemplatesData,
    t,
  } = params;

  const [newTemplateName, setNewTemplateName] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState('');

  const handleAddTemplate = useCallback(() => {
    const name = normalizeTemplateName(newTemplateName);
    if (!name) return;
    const existingIds = templateMeta.map((tpl) => tpl.id);
    const id = makeTemplateId(name, existingIds);
    const fallbackData =
      allTemplatesData[activeTemplate] || Object.values(allTemplatesData)[0] || defaultCV;
    setAllTemplatesData((prev) => ({ ...prev, [id]: cloneCvData(fallbackData) }));
    setTemplateMeta((prev) => [...prev, { id, name }]);
    setActiveTemplate(id);
    setNewTemplateName('');
  }, [
    newTemplateName,
    templateMeta,
    allTemplatesData,
    activeTemplate,
    setAllTemplatesData,
    setTemplateMeta,
    setActiveTemplate,
  ]);

  const handleStartRenameTemplate = useCallback((id: string, name: string) => {
    setEditingTemplateId(id);
    setEditingTemplateName(name);
  }, []);

  const handleCancelRenameTemplate = useCallback(() => {
    setEditingTemplateId(null);
    setEditingTemplateName('');
  }, []);

  const handleSaveRenameTemplate = useCallback(() => {
    if (!editingTemplateId) return;
    const name = normalizeTemplateName(editingTemplateName);
    if (!name) {
      handleCancelRenameTemplate();
      return;
    }
    setTemplateMeta((prev) =>
      prev.map((tpl) => (tpl.id === editingTemplateId ? { ...tpl, name } : tpl)),
    );
    handleCancelRenameTemplate();
  }, [editingTemplateId, editingTemplateName, handleCancelRenameTemplate, setTemplateMeta]);

  const handleDeleteTemplate = useCallback(
    (id: string) => {
      if (templateMeta.length <= 1) return;
      setTemplateMeta((prev) => {
        const deleted = prev.find((tpl) => tpl.id === id);
        const linkedId = deleted?.linkedVersionId;
        let next = prev.filter((tpl) => tpl.id !== id);
        if (linkedId) {
          next = next.map((tpl) =>
            tpl.id === linkedId ? { ...tpl, linkedVersionId: undefined } : tpl,
          );
        }
        const nextActive = next[0]?.id;
        if (activeTemplate === id && nextActive) {
          setActiveTemplate(nextActive);
        }
        return next;
      });
      setAllTemplatesData((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    [templateMeta, activeTemplate, setTemplateMeta, setAllTemplatesData, setActiveTemplate],
  );

  const handleDuplicateTemplate = useCallback(
    (id: string) => {
      const sourceMeta = templateMeta.find((tpl) => tpl.id === id);
      if (!sourceMeta) return;
      const existingIds = templateMeta.map((tpl) => tpl.id);
      const name = `${sourceMeta.name} (copie)`;
      const newId = makeTemplateId(name, existingIds);
      const sourceData =
        allTemplatesData[id] || Object.values(allTemplatesData)[0] || defaultCV;
      setAllTemplatesData((prev) => ({ ...prev, [newId]: cloneCvData(sourceData) }));
      setTemplateMeta((prev) => [...prev, { id: newId, name }]);
      setActiveTemplate(newId);
    },
    [templateMeta, allTemplatesData, setAllTemplatesData, setTemplateMeta, setActiveTemplate],
  );

  const renameTemplate = useCallback(
    (id: string, name: string) => {
      const normalized = normalizeTemplateName(name);
      if (!normalized) return;
      setTemplateMeta((prev) =>
        prev.map((tpl) => (tpl.id === id ? { ...tpl, name: normalized } : tpl)),
      );
    },
    [setTemplateMeta],
  );

  const handleImportJSON = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const text = await file.text();
        const raw = JSON.parse(text) as Record<string, unknown>;
        const { exportDate: _exportDate, ...rest } = raw;
        void _exportDate;
        const imported = rest as unknown as CVData;
        const pi = imported?.personalInfo;
        if (
          !pi ||
          typeof pi !== 'object' ||
          !Array.isArray(imported.experience) ||
          !Array.isArray(imported.education) ||
          !Array.isArray(imported.skills) ||
          !Array.isArray(imported.languages) ||
          !Array.isArray(imported.hobbies)
        ) {
          toast.error('Fichier JSON invalide : format backup ATSmyCV attendu.');
          return;
        }
        // Legacy compat: older backups stored a single `fullName` field.
        const r = pi as Record<string, unknown>;
        if (r.fullName && !r.firstName && !r.lastName) {
          const parts = String(r.fullName).trim().split(/\s+/);
          r.firstName = parts[0] || '';
          r.lastName = parts.slice(1).join(' ') || '';
        }
        const title = r.title as string | undefined;
        const name = normalizeTemplateName(title || 'Importé');
        const existingIds = templateMeta.map((tpl) => tpl.id);
        const newId = makeTemplateId(name || 'importe', existingIds);
        setAllTemplatesData((prev) => ({ ...prev, [newId]: imported }));
        setTemplateMeta((prev) => [
          ...prev,
          { id: newId, name: name || t('versions.imported') },
        ]);
        setActiveTemplate(newId);
      } catch (err) {
        console.error('Import backup JSON failed', err);
        toast.error("Impossible de lire le fichier. Vérifiez qu'il s'agit d'un backup JSON ATSmyCV.");
      }
    },
    [templateMeta, setAllTemplatesData, setTemplateMeta, setActiveTemplate, t],
  );

  return {
    newTemplateName,
    setNewTemplateName,
    editingTemplateId,
    editingTemplateName,
    setEditingTemplateName,
    handleAddTemplate,
    handleStartRenameTemplate,
    handleCancelRenameTemplate,
    handleSaveRenameTemplate,
    handleDeleteTemplate,
    handleDuplicateTemplate,
    handleImportJSON,
    renameTemplate,
  };
}
